import type { SalesTotal } from '../../data/sales-total';
import {
  formatPercent,
  preciseCurrencyFormatter,
  shortCurrencyFormatter,
} from '../../helpers/format-currency';
import type { DisplayType } from './types';

// Gross margin as a fraction of sales. Returns null for months with no sales,
// so the line breaks rather than crashing to 0% on empty/future months.
export const getMarginPercent = (total: SalesTotal): number | null => {
  if (total.sales === 0) {
    return null;
  }

  return (total.sales - total.costs) / total.sales;
};

export const getDisplayValue = (total: SalesTotal, displayType: DisplayType): number => {
  switch (displayType) {
    case 'Sales': {
      return total.sales;
    }

    case 'Transactions': {
      return total.transactions;
    }

    case 'Gross Margin': {
      return total.sales - total.costs;
    }

    case 'Average Order Value': {
      if (total.transactions === 0) {
        return 0;
      }

      return total.sales / total.transactions;
    }
  }
};

export const formatDisplayValue = (
  value: number | null | undefined,
  displayType: DisplayType,
  asPercent = false
): string => {
  if (asPercent) {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return 'N/A';
    }

    return formatPercent(value);
  }

  if (!value || Number.isNaN(value)) {
    return 'N/A';
  }

  if (displayType === 'Transactions') {
    return value.toLocaleString('en-US');
  }

  if (displayType === 'Average Order Value') {
    return preciseCurrencyFormatter(value);
  }

  return shortCurrencyFormatter(value);
};
